import moment from 'moment';
import {
  DayAvailabilityBlock,
  CalenderEvent,
  DayTimeOverlap,
  daysMap
} from 'model/v2/availabilityTemplate';
import { setShiftData } from 'model/v2/setShiftBlock';

export const checkForBlockOverlaps = (
  shiftData: any,
  providerSetShift: setShiftData[],
  existingEvents: CalenderEvent[],
  selectedDate: Date,
  excludeEventId?: string | number,
  excludeSchedulingShiftId?: number
): boolean => {
  const { block, isAdditionalShift } = shiftData;

  if (existingEvents.length === 0) {
    return false;
  }
  if (isAdditionalShift === undefined || isAdditionalShift === null) {
    return false;
  }

  const shiftInfo = providerSetShift.find(
    shift => shift.shiftId === Number(block)
  );

  if (!shiftInfo) {
    return false;
  }

  const dateToUse =
    selectedDate && moment(selectedDate).isValid() ? selectedDate : new Date();
  const selectedWeekStart = moment(dateToUse).startOf('week');

  // Check all blocks in the shift for overlaps
  for (const blockInfo of shiftInfo.blocks) {
    const daysNumArray = blockInfo.days;

    const startMoment = moment(blockInfo.startTime, 'HH:mm');
    const endMoment = moment(blockInfo.endTime, 'HH:mm');

    for (const day of daysNumArray) {
      const targetDate = selectedWeekStart.clone().add(day, 'days');

      const proposedStart = moment()
        .year(targetDate.year())
        .month(targetDate.month())
        .date(targetDate.date())
        .hour(startMoment.hour())
        .minute(startMoment.minute())
        .second(0)
        .millisecond(0)
        .toDate();

      const proposedEnd = moment()
        .year(targetDate.year())
        .month(targetDate.month())
        .date(targetDate.date())
        .hour(endMoment.hour())
        .minute(endMoment.minute())
        .second(0)
        .millisecond(0)
        .toDate();

      const hasOverlap = existingEvents.some(existingEvent => {
        const shouldExclude =
          (excludeEventId && existingEvent.id === excludeEventId) ||
          (excludeSchedulingShiftId &&
            existingEvent.schedulingShiftId === excludeSchedulingShiftId);

        if (shouldExclude) {
          return false;
        }

        if (existingEvent.start.getDay() !== day) {
          return false;
        }

        const isAvailabilityEvent =
          existingEvent.type === 'Block Availability' ||
          existingEvent.type === 'Additional Availability' ||
          existingEvent.type === 'working' ||
          existingEvent.title === 'Block Availability' ||
          existingEvent.title === 'Additional Availability';

        if (!isAvailabilityEvent) {
          return false;
        }

        const existingIsAdditional =
          existingEvent.isAdditionalShift ||
          existingEvent.type === 'Additional Availability';
        const proposedIsAdditional = isAdditionalShift;

        if (existingIsAdditional !== proposedIsAdditional) {
          const existingStart = existingEvent.start.getTime();
          const existingEnd = existingEvent.end.getTime();
          const proposedStartTime = proposedStart.getTime();
          const proposedEndTime = proposedEnd.getTime();

          const normalizeToMinute = (timestamp: number) => {
            const date = new Date(timestamp);
            return new Date(
              date.getFullYear(),
              date.getMonth(),
              date.getDate(),
              date.getHours(),
              date.getMinutes(),
              0,
              0
            ).getTime();
          };

          const normalizedExistingStart = normalizeToMinute(existingStart);
          const normalizedExistingEnd = normalizeToMinute(existingEnd);
          const normalizedProposedStart = normalizeToMinute(proposedStartTime);
          const normalizedProposedEnd = normalizeToMinute(proposedEndTime);

          const overlaps =
            (normalizedProposedStart < normalizedExistingEnd &&
              normalizedProposedEnd > normalizedExistingStart) ||
            (normalizedExistingStart < normalizedProposedEnd &&
              normalizedExistingEnd > normalizedProposedStart);

          if (overlaps) {
            console.log('🚨 OVERLAP DETECTED between different event types!', {
              existingEvent: existingEvent.title,
              existingTime: `${existingEvent.start.toLocaleTimeString()} - ${existingEvent.end.toLocaleTimeString()}`,
              proposedTime: `${proposedStart.toLocaleTimeString()} - ${proposedEnd.toLocaleTimeString()}`,
              existingType: existingEvent.type,
              proposedType: isAdditionalShift
                ? 'Additional Availability'
                : 'Block Availability'
            });
          }

          return overlaps;
        } else {
          const existingStart = existingEvent.start.getTime();
          const existingEnd = existingEvent.end.getTime();
          const proposedStartTime = proposedStart.getTime();
          const proposedEndTime = proposedEnd.getTime();

          const normalizeToMinute = (timestamp: number) => {
            const date = new Date(timestamp);
            return new Date(
              date.getFullYear(),
              date.getMonth(),
              date.getDate(),
              date.getHours(),
              date.getMinutes(),
              0,
              0
            ).getTime();
          };

          const normalizedExistingStart = normalizeToMinute(existingStart);
          const normalizedExistingEnd = normalizeToMinute(existingEnd);
          const normalizedProposedStart = normalizeToMinute(proposedStartTime);
          const normalizedProposedEnd = normalizeToMinute(proposedEndTime);

          // Check if there's any overlap in time
          const overlaps =
            (normalizedProposedStart < normalizedExistingEnd &&
              normalizedProposedEnd > normalizedExistingStart) ||
            (normalizedExistingStart < normalizedProposedEnd &&
              normalizedExistingEnd > normalizedProposedStart);

          if (overlaps) {
            console.log('🚨 OVERLAP DETECTED between same event types!', {
              existingEvent: existingEvent.title,
              existingTime: `${existingEvent.start.toLocaleTimeString()} - ${existingEvent.end.toLocaleTimeString()}`,
              proposedTime: `${proposedStart.toLocaleTimeString()} - ${proposedEnd.toLocaleTimeString()}`,
              eventType: existingEvent.type
            });
          }

          return overlaps;
        }
      });

      if (hasOverlap) {
        return true;
      }
    }
  }

  return false;
};

export const getOverlapDetails = (
  shiftData: any,
  providerSetShift: setShiftData[],
  existingEvents: CalenderEvent[],
  excludeEventId?: string | number,
  excludeSchedulingShiftId?: number
): Array<{
  day: string;
  proposedStart: string;
  proposedEnd: string;
  conflictingEvent: CalenderEvent;
}> => {
  const { block } = shiftData;

  const shiftInfo = providerSetShift.find(
    shift => shift.shiftId === Number(block)
  );

  if (!shiftInfo) return [];

  const dayNames = {
    1: 'Monday',
    2: 'Tuesday',
    3: 'Wednesday',
    4: 'Thursday',
    5: 'Friday',
    6: 'Saturday'
  };

  const overlaps: Array<{
    day: string;
    proposedStart: string;
    proposedEnd: string;
    conflictingEvent: CalenderEvent;
  }> = [];

  // Check all blocks in the shift for overlaps
  for (const blockInfo of shiftInfo.blocks) {
    const daysNumArray = blockInfo.days;
    const startMoment = moment(blockInfo.startTime, 'HH:mm');
    const endMoment = moment(blockInfo.endTime, 'HH:mm');

    daysNumArray.forEach((day: number) => {
      const dayName = dayNames[day as keyof typeof dayNames];

      const proposedStart = moment()
        .day(day)
        .hour(startMoment.hour())
        .minute(startMoment.minute());

      const proposedEnd = moment()
        .day(day)
        .hour(endMoment.hour())
        .minute(endMoment.minute());

      // Check for overlaps with existing events
      existingEvents.forEach(existingEvent => {
        if (
          (excludeEventId && existingEvent.id === excludeEventId) ||
          (excludeSchedulingShiftId &&
            existingEvent.schedulingShiftId === excludeSchedulingShiftId)
        ) {
          return;
        }

        if (existingEvent.start.getDay() !== day) {
          return;
        }

        const isAvailabilityEvent =
          existingEvent.type === 'Block Availability' ||
          existingEvent.type === 'Additional Availability' ||
          existingEvent.type === 'working' ||
          existingEvent.title === 'Block Availability' ||
          existingEvent.title === 'Additional Availability';

        if (!isAvailabilityEvent) {
          return;
        }

        const existingStart = existingEvent.start.getTime();
        const existingEnd = existingEvent.end.getTime();
        const proposedStartTime = proposedStart.toDate().getTime();
        const proposedEndTime = proposedEnd.toDate().getTime();

        const normalizeToMinute = (timestamp: number) => {
          const date = new Date(timestamp);
          return new Date(
            date.getFullYear(),
            date.getMonth(),
            date.getDate(),
            date.getHours(),
            date.getMinutes(),
            0,
            0
          ).getTime();
        };

        const normalizedExistingStart = normalizeToMinute(existingStart);
        const normalizedExistingEnd = normalizeToMinute(existingEnd);
        const normalizedProposedStart = normalizeToMinute(proposedStartTime);
        const normalizedProposedEnd = normalizeToMinute(proposedEndTime);

        if (
          (normalizedProposedStart < normalizedExistingEnd &&
            normalizedProposedEnd > normalizedExistingStart) ||
          (normalizedExistingStart < normalizedProposedEnd &&
            normalizedExistingEnd > normalizedProposedStart) ||
          (normalizedProposedStart === normalizedExistingStart &&
            normalizedProposedEnd === normalizedExistingEnd)
        ) {
          overlaps.push({
            day: dayName,
            proposedStart: proposedStart.format('HH:mm'),
            proposedEnd: proposedEnd.format('HH:mm'),
            conflictingEvent: existingEvent
          });
        }
      });
    });
  }

  return overlaps;
};

function getTimeInMinutes(time: string): number {
  const [hours, minutes] = time.split(':').map(Number);
  return hours * 60 + minutes;
}

function formatTime(minutes: number): string {
  const hours = Math.floor(minutes / 60)
    .toString()
    .padStart(2, '0');
  const mins = (minutes % 60).toString().padStart(2, '0');
  return `${hours}:${mins}`;
}
function groupByExactTimeRanges(
  blocks: DayAvailabilityBlock[]
): Record<string, number[]> {
  const rangeMap: Record<string, Set<number>> = {};

  for (const block of blocks) {
    const currentRange = `${block.startTime}-${block.endTime}`;

    if (!rangeMap[currentRange]) {
      rangeMap[currentRange] = new Set();
    }
    rangeMap[currentRange].add(block.dayOfWeek);

    // check which existing ranges are subsets of this block
    for (const existingRange of Object.keys(rangeMap)) {
      if (existingRange === currentRange) continue;

      const [exStart, exEnd] = existingRange.split('-');
      const [curStart, curEnd] = [block.startTime, block.endTime];

      if (exStart >= curStart && exEnd <= curEnd) {
        rangeMap[existingRange].add(block.dayOfWeek);
      }
    }
  }

  // Convert sets to arrays
  const result: Record<string, number[]> = {};
  for (const [key, value] of Object.entries(rangeMap)) {
    result[key] = Array.from(value).sort((a, b) => a - b);
  }

  return result;
}
export const getIntersectingTimeBlocks = (
  clientBlocks: DayAvailabilityBlock[],
  providerBlocks: DayAvailabilityBlock[]
) => {
  const overlapBlocks: DayAvailabilityBlock[] = [];

  clientBlocks.forEach(clientBlock => {
    const providerBlock = providerBlocks.find(
      p => p.dayOfWeek === clientBlock.dayOfWeek
    );

    if (providerBlock) {
      const clientStart = getTimeInMinutes(clientBlock.startTime);
      const clientEnd = getTimeInMinutes(clientBlock.endTime);
      const providerStart = getTimeInMinutes(providerBlock.startTime);
      const providerEnd = getTimeInMinutes(providerBlock.endTime);

      const overlapStart = Math.max(clientStart, providerStart);
      const overlapEnd = Math.min(clientEnd, providerEnd);

      if (overlapStart < overlapEnd) {
        overlapBlocks.push({
          dayOfWeek: clientBlock.dayOfWeek,
          startTime: formatTime(overlapStart),
          endTime: formatTime(overlapEnd)
        });
      }
    }
  });
  const overlapTimes = groupByExactTimeRanges(overlapBlocks);
  const result: DayTimeOverlap = {
    startTime: '',
    endTime: '',
    days: ''
  };
  if (Object.keys(overlapTimes).length > 0) {
    const sortedOverlapTimes = Object.entries(overlapTimes).sort(
      ([, daysA], [, daysB]) => daysB.length - daysA.length
    );
    const [topRange, topDays] = sortedOverlapTimes[0];
    const [startTime, endTime] = topRange.split('-');
    result.startTime = startTime;
    result.endTime = endTime;
    result.days = topDays.map(day => daysMap[day.toString()]).join('');
  }
  return result;
};
